import 'leaflet/dist/leaflet.css'
import './UDTMap.css'
import './MapFilters.css'

import React, { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import { useNavigate } from 'react-router-dom'
import { defaultMapCenter } from '@/env/config'
import { formatDate } from '@/common/time'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'

export interface UDTMapRow {
  imei: string
  dtTracker: string
  lat: number
  lng: number
  daysToUdt: number | null
  machine: {
    id: string
    name: string | null
    internalNumber: string | null
    udtExpirationDate: string | null
  }
}

export const udtGroups = ['expired', 'urgent', 'soon', 'valid', 'missing'] as const
export type UDTGroup = typeof udtGroups[number]

export function udtGroup(days: number | null): UDTGroup {
  if (days === null) return 'missing'
  if (days < 0) return 'expired'
  if (days <= 7) return 'urgent'
  if (days < 30) return 'soon'
  return 'valid'
}

export function FitUDTMap({ rows, filterKey }: { rows: UDTMapRow[], filterKey: string }) {
  const map = useMap()
  const latestRows = useRef(rows)
  latestRows.current = rows

  // GPS refreshes must not reset the user's viewport. Fit only on mount/filter change.
  useEffect(() => {
    const points = latestRows.current.map(row => L.latLng(row.lat, row.lng))
    if (points.length > 0) {
      map.fitBounds(L.latLngBounds(points), { padding: [24, 24], maxZoom: 13 })
    } else {
      map.setView(defaultMapCenter, 13)
    }
  }, [map, filterKey])

  return null
}

export function UDTMapComponent({ services, session }: { services: Services, session: CurrentSession }) {
  const [rows, setRows] = useState<UDTMapRow[]>()
  const [error, setError] = useState(false)
  const [reloadVersion, setReloadVersion] = useState(0)
  const [enabled, setEnabled] = useState<UDTGroup[]>([...udtGroups])
  const navigate = useNavigate()
  const t = (key: string) => session.translates.for(`map.udt.${key}`)

  useEffect(() => {
    let disposed = false
    let timer: ReturnType<typeof setTimeout>
    const controller = new AbortController()

    async function refresh() {
      try {
        const response = await services.session.fetchWithToken('/api/communal/udt/map', {
          signal: controller.signal,
        })
        if (!response.ok) throw new Error('Unable to load UDT map')
        const data: { rows: UDTMapRow[] } = await response.json()
        if (!disposed) {
          setRows(data.rows)
          setError(false)
        }
      } catch {
        if (!disposed) setError(true)
      } finally {
        if (!disposed) timer = setTimeout(refresh, 30000)
      }
    }

    void refresh()
    return () => {
      disposed = true
      clearTimeout(timer)
      controller.abort()
    }
  }, [services, reloadVersion])

  const counts = Object.fromEntries(udtGroups.map(group => [group, 0])) as Record<UDTGroup, number>
  rows?.forEach(row => { counts[udtGroup(row.daysToUdt)]++ })
  const visibleRows = rows?.filter(row => enabled.includes(udtGroup(row.daysToUdt))) ?? []
  const filterKey = udtGroups.filter(group => enabled.includes(group)).join(',')

  function remainingText(days: number | null): string {
    if (days === null) return t('missing')
    if (days === 0) return t('today')
    return t(days < 0 ? 'overdueDays' : 'remainingDays').replace('{days}', String(Math.abs(days)))
  }

  return <div className='udt-map-view'>
    <fieldset className='udt-map-legend map-filter-legend'>
      <legend>{t('legend')}</legend>
      {udtGroups.map(group => <label key={group} className='udt-map-filter map-filter-option'>
        <input type='checkbox' checked={enabled.includes(group)} onChange={() => {
          setEnabled(current => current.includes(group)
            ? current.filter(item => item !== group)
            : [...current, group])
        }} />
        <span className={`udt-map-dot udt-map-${group}`} aria-hidden='true' />
        <span>{t(group)} ({counts[group]})</span>
      </label>)}
    </fieldset>

    {error ? <div className='udt-map-error' role='alert'>
      <span>{t('loadError')}</span>
      <button type='button' onClick={() => setReloadVersion(version => version + 1)}>{t('retry')}</button>
    </div> : null}

    {rows === undefined ? (!error ? <div className='spinner' role='status' aria-label={t('loading')} /> : null) : <>
      {visibleRows.length === 0 ? <p className='udt-map-empty' role='status'>{t('empty')}</p> : null}
      <div className='udt-map-canvas'>
        <MapContainer center={defaultMapCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
          <TileLayer attribution='&copy; OpenStreetMap contributors' url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
          <FitUDTMap rows={visibleRows} filterKey={filterKey} />
          {visibleRows.map(row => <Marker key={row.machine.id} position={[row.lat, row.lng]}
            title={`${row.machine.name || row.machine.internalNumber || row.imei}: ${remainingText(row.daysToUdt)}`}
            icon={L.divIcon({
              className: 'udt-map-marker',
              html: `<span class="udt-map-dot udt-map-${udtGroup(row.daysToUdt)}"></span>`,
              iconSize: [22, 22],
              iconAnchor: [11, 11],
            })}>
            <Popup>
              <div className='udt-map-popup'>
                <strong>{row.machine.name || row.machine.internalNumber || row.imei}</strong>
                <p>{session.translates.for('map.internal_number')}: {row.machine.internalNumber || '—'}</p>
                <p>{t('deadline')}: {row.machine.udtExpirationDate || t('missing')}</p>
                <p>{remainingText(row.daysToUdt)}</p>
                <p className='map-point-dttracker'>{session.translates.for('map.dt_tracker')}: {formatDate(new Date(row.dtTracker))}</p>
                <button className='main-btn' type='button' onClick={() => navigate(`/communal/machine/${row.machine.id}`)}>
                  {session.translates.for('map.machine_page')}
                </button>
              </div>
            </Popup>
          </Marker>)}
        </MapContainer>
      </div>
    </>}
  </div>
}
